import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Field } from '@app/models/module-structure';
import { RecordLine, SpecialLines } from '@app/layout/record-model';

export type DocumentLineDrawerRowType = 'item' | 'text' | 'subtotal';

export interface DocumentLineDrawerRow {
  key: string;
  rowNumber: number;
  rowType: DocumentLineDrawerRowType;
  values: { [propertyName: string]: string };
  searchText: string;
}

@Component({
  selector: 'app-document-lines-drawer',
  templateUrl: './document-lines-drawer.component.html',
  styleUrls: ['./document-lines-drawer.component.less'],
})
export class DocumentLinesDrawerComponent implements OnChanges {
  @Input() lines: Array<RecordLine | SpecialLines> = [];
  @Input() fields: Field[] = [];
  @Input() tableWidth = 0;

  searchText = '';
  rows: DocumentLineDrawerRow[] = [];
  filteredRows: DocumentLineDrawerRow[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lines || changes.fields) {
      this.rows = DocumentLinesDrawerComponent.buildRows(
        this.lines || [],
        this.fields || [],
      );
      this.applySearch();
    }
  }

  applySearch(): void {
    this.filteredRows = DocumentLinesDrawerComponent.filterRows(
      this.rows,
      this.searchText,
    );
  }

  trackByRowKey(_: number, row: DocumentLineDrawerRow): string {
    return row.key;
  }

  trackByFieldName(_: number, field: Field): string {
    return field.PropertyName;
  }

  static buildRows(
    lines: Array<RecordLine | SpecialLines>,
    fields: Field[],
  ): DocumentLineDrawerRow[] {
    return (lines || []).map((line, index) => {
      if (DocumentLinesDrawerComponent.isSpecialLine(line)) {
        return DocumentLinesDrawerComponent.buildSpecialLineRow(
          line,
          fields || [],
          index,
        );
      }

      return DocumentLinesDrawerComponent.buildItemLineRow(
        line,
        fields || [],
        index,
      );
    });
  }

  static filterRows(
    rows: DocumentLineDrawerRow[],
    searchText: string,
  ): DocumentLineDrawerRow[] {
    const normalizedSearch = DocumentLinesDrawerComponent.normalizeSearchValue(
      searchText,
    );

    if (normalizedSearch === '') {
      return [...(rows || [])];
    }

    return (rows || []).filter((row) =>
      row.searchText.includes(normalizedSearch),
    );
  }

  private static buildItemLineRow(
    line: RecordLine,
    fields: Field[],
    index: number,
  ): DocumentLineDrawerRow {
    const values = {};
    const searchParts = [
      (index + 1).toString(),
      'item',
      DocumentLinesDrawerComponent.toDisplayValue(line.Id),
    ];

    fields.forEach((field) => {
      const property = line.Properties?.find(
        (item) => item.Name === field.PropertyName,
      );
      const value = DocumentLinesDrawerComponent.toDisplayValue(
        property?.ValueAsObject != null && property?.ValueAsObject !== ''
          ? property.ValueAsObject
          : property?.Value,
      );

      values[field.PropertyName] = value;
      searchParts.push(value);
    });

    return {
      key: `line-${line.Id}-${index}`,
      rowNumber: index + 1,
      rowType: 'item',
      values,
      searchText: DocumentLinesDrawerComponent.normalizeSearchValue(
        searchParts.join(' '),
      ),
    };
  }

  private static buildSpecialLineRow(
    line: SpecialLines,
    fields: Field[],
    index: number,
  ): DocumentLineDrawerRow {
    const values = {};
    const firstField = fields[0];
    const lineTotalField = fields.find((field) => field.PropertyName === 'LineTotal');
    const rowType = line.LineType === 'S' ? 'subtotal' : 'text';
    const rowLabel = rowType === 'subtotal' ? 'Subtotal' : 'Text';
    const lineText = DocumentLinesDrawerComponent.toDisplayValue(line.LineText);

    if (firstField != null) {
      values[firstField.PropertyName] =
        rowType === 'subtotal' ? rowLabel : lineText;
    }

    if (rowType === 'subtotal' && lineTotalField != null) {
      values[lineTotalField.PropertyName] = lineText;
    }

    return {
      key: `special-${line.Id}-${line.LineSeq}-${index}`,
      rowNumber: index + 1,
      rowType,
      values,
      searchText: DocumentLinesDrawerComponent.normalizeSearchValue(
        [index + 1, rowLabel, line.LineType, lineText].join(' '),
      ),
    };
  }

  private static isSpecialLine(
    line: RecordLine | SpecialLines,
  ): line is SpecialLines {
    return (line as SpecialLines).LineSeq != null;
  }

  private static normalizeSearchValue(value: any): string {
    return value == null ? '' : value.toString().trim().toLowerCase();
  }

  private static toDisplayValue(value: any): string {
    if (value == null) {
      return '';
    }

    if (value instanceof Date) {
      return value.toLocaleDateString();
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return value.toString();
      }
    }

    return value.toString();
  }
}
